import { Injectable } from '@angular/core';
import {
  FormBuilder, FormControl, FormGroup, UntypedFormArray, UntypedFormControl, UntypedFormGroup
} from '@angular/forms';
import { ConnectionFormValue } from 'entity/forms/create-connection-form/create-connection-form.component';

import {
  CustomFormDetailsFragment, CustomFormFieldDetailsFragment, CustomFormFieldObjectTypeTypes,
  EntityTypeDetailsFragment, GetEntityById2
} from 'generated/graphql';

@Injectable({
  providedIn: 'root'
})
export class CustomFormBuilderServiceFactory {
  constructor(private fb: FormBuilder) { }

  static extractIdFromKey(key: string): string {
    return key.split('-')[0];
  }

  static extractFieldObjectIdFromKey(key: string): string {
    return key.split('-')[2];
  }

  static getFormFieldSettingsFromControlName(key: string, customForm: CustomFormDetailsFragment): CustomFormFieldDetailsFragment {
    const id = CustomFormBuilderServiceFactory.extractIdFromKey(key);
    return customForm.fields.find(field => field.id === id);
  }

  createFormBuilder(customForm: CustomFormDetailsFragment, entityType: EntityTypeDetailsFragment, isCreateEntityOperation: boolean = false): CustomFormBuilderService {
    return new CustomFormBuilderService(customForm, entityType, isCreateEntityOperation);
  }
}

export class CustomFormBuilderService {

  form = this.createFormGroup();

  constructor(
    private customForm: CustomFormDetailsFragment,
    public entityType: EntityTypeDetailsFragment,
    public isCreateEntityOperation: boolean
  ) { }

  createFormGroup(): FormGroup {
    if (this.form) {
      return this.form;
    }

    const group = {};
    this.customForm.fields.forEach(field => {
      // You can add more logic here based on your specific setting properties
      group[CustomFormBuilderService.makeFormControlKey(field)] = this.makeFormControl(field); // default value, you can add validators here
    });
    return new UntypedFormGroup(group);
  }

  getFormFieldSettingsFromControlName(formControlName: string): CustomFormFieldDetailsFragment {
    const id = CustomFormBuilderServiceFactory.extractIdFromKey(formControlName);
    return this.customForm.fields.find(field => field.id === id);
  }

  private getEntityControl(attribute: string) {
    const ctrolName = Object.keys(this.form.controls).find((key) => {
      const settings = this.getFormFieldSettingsFromControlName(key);
      if (settings.fieldObjectType === CustomFormFieldObjectTypeTypes.Entity
        && settings.objectAttribute === attribute) {
        return true;
      }
    });

    if (!ctrolName) {
      return null;
    }

    return this.form.get(ctrolName);
  }

  /**
   * The key is a combination of the field id, the field object type, and the field object id.
   * and object attribute. This is to ensure that the key is unique for each field.
   * But wouldn't it be unique on the id?
   * @param field The custom form field
   * @returns string
   */
  private static makeFormControlKey(field: CustomFormFieldDetailsFragment): string {
    return `${field.id}-${field.fieldObjectType}-${field.fieldObjectId}-oa${field.objectAttribute}`;
  }

  private makeFormControl(field: CustomFormFieldDetailsFragment) {
    if (field.fieldObjectType === 'TagCategory') {
      if (field.tagCategory.multiselectable) {
        return new UntypedFormArray([]);
      }
      return new UntypedFormArray([new UntypedFormControl()]);
    }

    if (field.fieldObjectType === CustomFormFieldObjectTypeTypes.Entity) {
      if (field.objectAttribute === 'name') {
        return new FormControl<{
          firstName: string;
          lastName: string;
          middleName: string;
          nickname: string;
        }>(null);
      }

      if (field.objectAttribute === 'demographics') {
        return new FormControl<{
          dateOfBirth: string;
          age: any;
        }>(null);
      }

      if (field.objectAttribute === 'language') {
        return new FormControl<{
          entityLanguages: { languageCode: string; preferred: boolean }[];
        }>(null);
      }
    };

    // Add support for EntityConnection fields
    if (field.fieldObjectType === CustomFormFieldObjectTypeTypes.EntityConnectionType) {
      return new FormControl<ConnectionFormValue>(null);
    }

    return new UntypedFormControl();

  }

  patchFromEntity(entity: GetEntityById2.GetEntity) {
    const form = this.form;

    this.getEntityControl('name')?.patchValue({
      firstName: entity.firstName,
      lastName: entity.lastName,
      middleName: entity.middleName,
      nickname: entity.nickname,
      pronouns: entity.pronouns,
    }, { emitEvent: false });

    this.getEntityControl('demographics')?.patchValue({
      dateOfBirth: entity.dateOfBirth,
      age: entity.age,
    }, { emitEvent: false });

    this.getEntityControl('language')?.patchValue({
      entityLanguages: (entity.entityLanguages || []).map(el => ({
        languageCode: el.languageCode,
        preferred: !!el.preferred
      }))
    }, { emitEvent: false });
  }
}
